import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import allApi from "../../../../api/allApi";
import { useLocalization } from "../../localization/LocalizationContext";
import {
  isFlagOn,
  isSsidName,
  isVlanId,
  isWpaPassword,
  needsWpaPassword,
  toFlag,
} from "../../common/configValidation";
import { fontSize } from "@mui/system";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";

function WifiSetting() {
  const { localizationStrings } = useLocalization();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("wifi");
  const [allBands, setAllBands] = useState([]);
  const [vlans, setVlans] = useState([]);
  const [showManualModal, setShowManualModal] = useState(false);
  const [manualDevice, setManualDevice] = useState({ name: "", mac: "" });
  const [addDeviceCollapse, setAddDeviceCollapse] = useState({
    connected: false,
    recent: false,
    allowed: false,
  });
  const [showAddDeviceModal, setShowAddDeviceModal] = useState(false);
  const [recentTime, setRecentTime] = useState("1 Hour");
  const [macAclStatus, setMacAclStatus] = useState(0);
  const [ssidDetails, setSsidDetails] = useState({
    ssid: "",
    broadcastStatus: false,
    band: [],
    networkName: "",
    vlanId: "",
    bandSteeringSt: "",
    fastRoamingSt: "",
    clientIsolation: "",
    allowLocalUIAccess: "",
    scheduleEnabled: "",
    kvrStatus: "",
    securityAuthentication: "",
    multiPsK: false,
    password: "",
    selectedSchedule: "",
    schedule: "",
    ieee80211w: "",
    wnId: "",
    networkId: "",
    ssidSchedule: "",
  });
  const [remoteAclMode, setRemoteAclMode] = useState(0);
  const [macList, setMacList] = useState([]);
  const [enableMacAcl, setEnableMacAcl] = useState(false);
  const [selectedMacs, setSelectedMacs] = useState([]);
  const [showMacDropdown, setShowMacDropdown] = useState(false);
  const [errorPopup, setErrorPopup] = useState({
    show: false,
    title: "",
    message: "",
  });
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    fetchMacList();
  }, []);

  useEffect(() => {
    if (!location.state?.ssidDetails) return;
    const ssidData = location.state.ssidDetails;

    // Dynamic bands
    const bandsFromApi = ssidData.band
      ? ssidData.band.split(",").map((b) => b.trim())
      : [];
    setAllBands(bandsFromApi);

    // Dynamic VLANs
    const vlanList =
      ssidData.vlans || (ssidData.vlanId ? [ssidData.vlanId] : []);
    setVlans(vlanList);

    setSsidDetails({
      ssid: ssidData.ssid || "",
      broadcastStatus:
        ssidData.broadcastStatus === "1" || ssidData.broadcastStatus === 1,
      band: bandsFromApi,
      networkName: ssidData.networkName || ssidData.networkId || "",
      vlanId: ssidData.vlanId || "",
      bandSteeringSt: String(ssidData.bandSteeringSt ?? "0"),
      fastRoamingSt: String(ssidData.fastRoamingSt ?? "0"),
      clientIsolation: String(ssidData.clientIsolation ?? "0"),
      allowLocalUIAccess: String(
        ssidData.allowLocalUIAccess ?? ssidData.AllowLocalUIAccess ?? "0"
      ),
      scheduleEnabled: String(ssidData.scheduleEnabled ?? "0"),
      kvrStatus: String(ssidData.kvrStatus ?? "0"),
      securityAuthentication: ssidData.securityAuthentication || "",
      multiPsK: ssidData.multiPsK || false,
      password: ssidData.password || "",
      selectedSchedule: ssidData.selectedSchedule || "",
      schedule: ssidData.schedule || "",
      ieee80211w: ssidData.ieee80211w || "0",
      wnId: ssidData.wnId || "",
      networkId: ssidData.networkId || "",
      ssidSchedule: ssidData.ssidSchedule || "",
    });
  }, [location.state]);

  useEffect(() => {
    if (ssidDetails.wnId) {
      fetchMacList();
    }
  }, [ssidDetails.wnId]);

  const handleInputChange = (e) => {
    const { name, type, checked, value } = e.target;
    setSsidDetails((prevDetails) => ({
      ...prevDetails,
      [name]:
        type === "checkbox"
          ? name === "broadcastStatus" || name === "multiPsK"
            ? checked
            : checked
              ? "1"
              : "0"
          : value,
    }));
  };

  const handleBandChange = (e) => {
    const { value, checked } = e.target;
    setSsidDetails((prevDetails) => {
      if (value === "All") {
        return { ...prevDetails, band: checked ? ["2.4 GHz", "5 GHz"] : [] };
      }
      const band = prevDetails.band.filter((item) => item !== "All" && item !== value);
      if (checked) band.push(value);
      return { ...prevDetails, band };
    });
  };

  const isSsidFormValid = () => {
    if (!isSsidName(ssidDetails.ssid)) return false;
    if (!Array.isArray(ssidDetails.band) || ssidDetails.band.length === 0) return false;
    if (!isVlanId(ssidDetails.vlanId)) return false;
    if (!String(ssidDetails.securityAuthentication || "").trim()) return false;
    if (
      needsWpaPassword(ssidDetails.securityAuthentication) &&
      !isWpaPassword(ssidDetails.password)
    ) {
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    try {
      if (!isSsidFormValid()) {
        setErrorPopup({
          show: true,
          title: "Invalid SSID settings",
          message:
            "Check SSID name (1-32), band, VLAN, security, and a password of 8-63 characters for WPA Personal.",
        });
        return;
      }

      const payload = {
        wnId: ssidDetails.wnId,
        ssid: ssidDetails.ssid.trim(),
        networkId: ssidDetails.networkId,
        broadcastStatus: toFlag(ssidDetails.broadcastStatus),
        band: ssidDetails.band.filter((item) => item !== "All").join(", "),
        vlanId: ssidDetails.vlanId,
        bandSteeringSt: toFlag(ssidDetails.bandSteeringSt),
        fastRoamingSt: toFlag(ssidDetails.fastRoamingSt),
        clientIsolation: toFlag(ssidDetails.clientIsolation),
        allowLocalUIAccess: toFlag(ssidDetails.allowLocalUIAccess),
        securityAuthentication: ssidDetails.securityAuthentication,
        password: ssidDetails.password,
        ieee80211w: ssidDetails.ieee80211w || "0",
        kvrStatus: toFlag(ssidDetails.kvrStatus),
        scheduleEnabled: toFlag(ssidDetails.scheduleEnabled),
      };
      const api = new allApi();
      const response = await api.putRequest(
        `api/wireless/configuration/editSsid?networkId=${ssidDetails.networkId}`,
        payload
      );
      if (!response || response.error) {
        throw new Error(response.error || "Failed to update SSID");
      }
      setSuccessMessage("SSID updated successfully");
      setShowSuccess(true);
    } catch (error) {
      console.error("Error:", error);
      setErrorPopup({
        show: true,
        title: "Error updating SSID",
        message: error.message,
      });
    }
  };

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
  };

  const tabTitles = {
    wifisetting: "WiFi Settings",
    macacl: "MAC ACL",
    trafficPolicies: "Traffic Policies",
  };

  const currentTitle = tabTitles[activeTab] || "WiFi Settings";

  const fetchMacList = async () => {
    try {
      if (!ssidDetails.wnId) return;

      const api = new allApi();
      const res = await api.getRequest(
        `api/wireless/configuration/allClient/${ssidDetails.wnId}`
      );

      if (res && Array.isArray(res.response)) {
        const macs = [
          ...new Set(res.response.map((item) => item.mac).filter(Boolean)),
        ];

        setMacList(macs);
      } else {
        console.warn("Unexpected MAC API response:", res);
        setMacList([]);
      }
    } catch (error) {
      console.error("Failed to fetch MAC list:", error);
      setMacList([]);
    }
  };

  const handleMacAclSave = async () => {
    try {
      if (!ssidDetails.wnId) {
        alert("WN ID not found");
        return;
      }

      const api = new allApi();

      const payload = {
        macAclStatus: macAclStatus,
        remoteAccessControlMode: remoteAclMode,
        macList: selectedMacs,
      };

      console.log("MAC ACL Payload:", payload);

      const response = await api.putRequest(
        `api/wireless/configuration/macAclSettings?wnId=${ssidDetails.wnId}`,
        payload
      );

      if (!response || response.error) {
        throw new Error(response.error || "Failed to update MAC ACL settings");
      }

      setSuccessMessage(
        response?.response?.message || "MAC ACL Setting Applied"
      );
      setShowSuccess(true);
    } catch (error) {
      console.error(error);
      alert("Error updating MAC ACL: " + error.message);
    }
  };

  const handleMacToggle = (mac) => {
    setSelectedMacs((prev) =>
      prev.includes(mac) ? prev.filter((m) => m !== mac) : [...prev, mac]
    );
  };

  return (
    <div className="nms-dash config-page">
      <div className="inc-card ch-100">
        <div className="inc-card-title">
          <h3>{currentTitle}</h3>
        </div>
        <div className="inc-card-body">
          <div className="sideTab-container">
            <div className="inc-side-tab">
              <ul className="nav-side" id="pills-tab" role="tablist">
                <li>
                  <button
                    className={`config-nav-item ${
                      activeTab === "wifi" ? "active" : ""
                    }`}
                    onClick={() => handleTabChange("wifi")}
                    role="tab"
                    aria-controls="pills-wifi"
                    aria-selected={activeTab === "wifi"}
                  >
                    {localizationStrings?.settings}{" "}
                    <i className="fa fa-angle-right" aria-hidden="true"></i>
                  </button>
                </li>
                <li>
                  <button
                    className={`config-nav-item ${
                      activeTab === "macacl" ? "active" : ""
                    }`}
                    onClick={() => handleTabChange("macacl")}
                    role="tab"
                    aria-controls="pills-macacl"
                    aria-selected={activeTab === "macacl"}
                  >
                    MAC ACL{" "}
                    <i className="fa fa-angle-right" aria-hidden="true"></i>
                  </button>
                </li>
                <li>
                  <button
                    className={`config-nav-item ${
                      activeTab === "trafficPolicies" ? "active" : ""
                    }`}
                    onClick={() => handleTabChange("trafficPolicies")}
                    role="tab"
                    aria-controls="pills-traffic"
                    aria-selected={activeTab === "trafficPolicies"}
                  >
                    {localizationStrings?.trafficPolicies}{" "}
                    <i className="fa fa-angle-right" aria-hidden="true"></i>
                  </button>
                </li>
              </ul>
            </div>
            <div className="inc-side-tabcontainer">
              <div className="tab-content" id="pills-tabContent">
                {activeTab === "wifi" && (
                  <div
                    className="tab-pane fade show active"
                    id="pills-wifi"
                    role="tabpanel"
                    aria-labelledby="pills-wifi-tab"
                  >
                    <div className="add-ssid-body editssid-scroll">
                      <h4>{localizationStrings?.wirelessSettings}</h4>
                      <div className="row d-flex">
                        <div className="col-lg-6">
                          <input
                            type="text"
                            className="form-control"
                            name="ssid"
                            placeholder={localizationStrings?.ssid}
                            value={ssidDetails.ssid}
                            onChange={handleInputChange}
                          />
                        </div>
                        <div className="col-lg-6">
                          <div className="switch-box">
                            <strong>
                              {localizationStrings?.broadcastSSID}
                            </strong>
                            <label className="ice-switch">
                              <input
                                type="checkbox"
                                name="broadcastStatus"
                                checked={ssidDetails.broadcastStatus}
                                onChange={handleInputChange}
                              />
                              <span className="slider"></span>
                            </label>
                          </div>
                        </div>
                      </div>

                      <div className="border-line"></div>

                      <h4 className="mt-3">
                        {localizationStrings?.band}
                        <span className="text-danger">*</span>
                      </h4>
                      <p>{localizationStrings?.wpaInfo}</p>
                      <div className="row">
                        <div className="col-lg-3 checkbox-group">
                          <label className="checkbox">
                            <input
                              type="checkbox"
                              name="band"
                              value="All"
                              checked={ssidDetails.band.length === 2}
                              onChange={handleBandChange}
                            />
                            <span className="indicator"></span>
                            <strong>{localizationStrings?.all}</strong>
                          </label>
                        </div>
                        <div className="col-lg-3 checkbox-group">
                          <label className="checkbox">
                            <input
                              type="checkbox"
                              name="band"
                              value="2.4 GHz"
                              checked={ssidDetails.band.includes("2.4 GHz")}
                              onChange={handleBandChange}
                            />
                            <span className="indicator"></span>
                            <strong>2.4 GHz</strong>
                          </label>
                        </div>
                        <div className="col-lg-3 checkbox-group">
                          <label className="checkbox">
                            <input
                              type="checkbox"
                              name="band"
                              value="5 GHz"
                              checked={ssidDetails.band.includes("5 GHz")}
                              onChange={handleBandChange}
                            />
                            <span className="indicator"></span>
                            <strong>5 GHz</strong>
                          </label>
                        </div>
                      </div>

                      <div className="border-line"></div>

                      <h4 className="mt-3">
                        {localizationStrings?.securitySettings}
                      </h4>
                      <div className="row">
                        <div className="col-lg-6">
                          <label
                            htmlFor="securityAuthentication"
                            className="col-form-label"
                          >
                            {localizationStrings?.security}
                            <span className="text-danger">*</span>
                          </label>
                          <select
                            className="form-select"
                            name="securityAuthentication"
                            value={ssidDetails.securityAuthentication}
                            onChange={handleInputChange}
                          >
                            <option value="">
                              {localizationStrings?.selectSecurity}
                            </option>
                            <option value="Open">
                              {localizationStrings?.open}
                            </option>
                            <option value="WPA2 Personal">
                              {localizationStrings?.WPA2Personal}
                            </option>
                            <option value="WPA2 Personal Mixed">
                              {localizationStrings?.WPA2PersonalMixed}
                            </option>
                            <option value="WPA3 Personal">
                              {localizationStrings?.WPA3Personal}
                            </option>
                            <option value="WPA3 Personal Mixed">
                              {localizationStrings?.WPA3PersonalMixed}
                            </option>
                          </select>
                        </div>
                      </div>

                      <div className="border-line"></div>
                      <div className="row mt-3">
                        <div className="col-lg-12">
                          <label
                            className="col-form-label"
                            style={{ marginBottom: "8px" }}
                          >
                            802.11w (PMF)
                          </label>
                          <div className="d-flex">
                            <div className="col-lg-4">
                              <div className="switch-box">
                                <strong>Mandatory</strong>
                                <label className="ice-switch">
                                  <input
                                    type="radio"
                                    name="ieee80211w"
                                    value="2"
                                    checked={ssidDetails.ieee80211w === "2"}
                                    onChange={handleInputChange}
                                  />
                                  <span className="slider"></span>
                                </label>
                              </div>
                            </div>
                            <div className="col-lg-4">
                              <div className="switch-box">
                                <strong>Optional</strong>
                                <label className="ice-switch">
                                  <input
                                    type="radio"
                                    name="ieee80211w"
                                    value="1"
                                    checked={ssidDetails.ieee80211w === "1"}
                                    onChange={handleInputChange}
                                  />
                                  <span className="slider"></span>
                                </label>
                              </div>
                            </div>
                            <div className="col-lg-4">
                              <div className="switch-box">
                                <strong>Disable</strong>
                                <label className="ice-switch">
                                  <input
                                    type="radio"
                                    name="ieee80211w"
                                    value="0"
                                    checked={ssidDetails.ieee80211w === "0"}
                                    onChange={handleInputChange}
                                  />
                                  <span className="slider"></span>
                                </label>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {(ssidDetails.securityAuthentication ===
                        "WPA2 Personal" ||
                        ssidDetails.securityAuthentication ===
                          "WPA2 Personal Mixed") && (
                        <>
                          <div className="row mt-3">
                            <div className="col-lg-12">
                              <div className="switch-box">
                                <strong>
                                  {localizationStrings?.multiPresharedKey}
                                </strong>
                                <label className="ice-switch">
                                  <input
                                    type="checkbox"
                                    name="multiPsK"
                                    checked={ssidDetails.multiPsK}
                                    onChange={() =>
                                      setSsidDetails((prev) => ({
                                        ...prev,
                                        multiPsK: !prev.multiPsK,
                                      }))
                                    }
                                  />
                                  <span className="slider"></span>
                                </label>
                              </div>
                            </div>
                          </div>
                          <div className="row mt-3">
                            <div className="col-lg-6">
                              <label
                                htmlFor="password"
                                className="col-form-label"
                              >
                                {localizationStrings?.password}
                                <span className="text-danger">*</span>
                              </label>
                              <input
                                type="text"
                                className="form-control"
                                name="password"
                                placeholder={localizationStrings?.password}
                                value={ssidDetails.password}
                                onChange={handleInputChange}
                              />
                              {ssidDetails.password &&
                                !isWpaPassword(ssidDetails.password) && (
                                  <p className="text-danger mt-1">
                                    Password must be 8-63 characters.
                                  </p>
                                )}
                            </div>
                          </div>
                        </>
                      )}

                      {(ssidDetails.securityAuthentication ===
                        "WPA3 Personal" ||
                        ssidDetails.securityAuthentication ===
                          "WPA3 Personal Mixed") && (
                        <div className="row mt-3">
                          <div className="col-lg-6">
                            <label
                              htmlFor="password"
                              className="col-form-label"
                            >
                              {localizationStrings?.password}
                              <span className="text-danger">*</span>
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              name="password"
                              placeholder={localizationStrings?.password}
                              value={ssidDetails.password}
                              onChange={handleInputChange}
                            />
                            {ssidDetails.password &&
                              !isWpaPassword(ssidDetails.password) && (
                                <p className="text-danger mt-1">
                                  Password must be 8-63 characters.
                                </p>
                              )}
                          </div>
                        </div>
                      )}

                      <div className="border-line"></div>
                      <h4 className="mt-3">
                        {localizationStrings?.networkSettings}
                      </h4>
                      <div className="row">
                        <div className="col-lg-6">
                          <label className="col-form-label">
                            {localizationStrings?.networkId}
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            name="networkId"
                            value={ssidDetails.networkId}
                            readOnly
                          />
                        </div>
                        <div className="col-lg-6">
                          <label htmlFor="vlanId" className="col-form-label">
                            {localizationStrings?.vlanId}
                            <span className="text-danger">*</span>
                          </label>
                          <select
                            className="form-select"
                            name="vlanId"
                            value={ssidDetails.vlanId}
                            onChange={handleInputChange}
                          >
                            <option value="">
                              {localizationStrings?.selectVlan}
                            </option>
                            {vlans.map((vlan) => (
                              <option key={vlan} value={vlan}>
                                VLAN {vlan}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="border-line"></div>
                      <div className="row mt-3">
                        <div className="col-lg-12">
                          <div className="switch-box">
                            <strong>
                              {localizationStrings?.clientIsolation}
                              <span className="text-danger">*</span>
                            </strong>
                            <label className="ice-switch">
                              <input
                                type="checkbox"
                                name="clientIsolation"
                                checked={isFlagOn(ssidDetails.clientIsolation)}
                                onChange={handleInputChange}
                              />
                              <span className="slider"></span>
                            </label>
                          </div>
                        </div>
                      </div>
                      <div className="border-line"></div>
                      <div className="row mt-3">
                        <div className="col-lg-12">
                          <div className="switch-box">
                            <strong>
                              {localizationStrings?.accessToLocalUI}
                              <span className="text-danger">*</span>
                            </strong>
                            <label className="ice-switch">
                              <input
                                type="checkbox"
                                name="allowLocalUIAccess"
                                checked={isFlagOn(ssidDetails.allowLocalUIAccess)}
                                onChange={handleInputChange}
                                disabled={!isFlagOn(ssidDetails.clientIsolation)}
                              />
                              <span className="slider"></span>
                            </label>
                          </div>
                        </div>
                      </div>

                      <div className="border-line"></div>
                      <h4 className="mt-3 mb-4">
                        {localizationStrings?.radioSettings}
                      </h4>
                      <div className="row">
                        <div className="col-lg-4">
                          <div className="switch-box">
                            <strong>
                              {localizationStrings?.bandSteering}
                              <span className="text-danger">*</span>
                            </strong>
                            <label className="ice-switch">
                              <input
                                type="checkbox"
                                name="bandSteeringSt"
                                checked={isFlagOn(ssidDetails.bandSteeringSt)}
                                onChange={handleInputChange}
                              />
                              <span className="slider"></span>
                            </label>
                          </div>
                        </div>
                        <div className="col-lg-4 mb-3">
                          <div className="switch-box fast-roaming-box">
                            <div className="label-wrap">
                              <strong>
                                {localizationStrings?.fastRoaming}
                              </strong>
                              <div className="sub-label">
                                (802.11r) <span className="text-danger">*</span>
                              </div>
                            </div>
                            <label className="ice-switch">
                              <input
                                type="checkbox"
                                name="fastRoamingSt"
                                checked={isFlagOn(ssidDetails.fastRoamingSt)}
                                onChange={handleInputChange}
                              />
                              <span className="slider"></span>
                            </label>
                          </div>
                        </div>
                        <div className="col-lg-4">
                          <div className="switch-box">
                            <strong>802.11k/v</strong>
                            <label className="ice-switch">
                              <input
                                type="checkbox"
                                name="kvrStatus"
                                checked={isFlagOn(ssidDetails.kvrStatus)}
                                onChange={handleInputChange}
                              />
                              <span className="slider"></span>
                            </label>
                          </div>
                        </div>
                      </div>

                      <div className="border-line"></div>
                      <div className="row mt-3">
                        <div className="col-lg-12">
                          <div className="switch-box">
                            <strong>
                              {localizationStrings?.enableSSIDSchedule}
                              <span className="text-danger">*</span>
                            </strong>
                            <label className="ice-switch">
                              <input
                                type="checkbox"
                                name="scheduleEnabled"
                                checked={isFlagOn(ssidDetails.scheduleEnabled)}
                                onChange={handleInputChange}
                              />
                              <span className="slider"></span>
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="wifi-footer">
                      <button
                        type="button"
                        className="btn btn-outline"
                        onClick={() => navigate(-1)}
                      >
                        {localizationStrings?.cancel}
                      </button>
                      <button
                        type="button"
                        className="text-btn primary-btn"
                        onClick={handleSave}
                        disabled={!isSsidFormValid()}
                      >
                        {localizationStrings?.save}
                      </button>
                    </div>
                  </div>
                )}

                {activeTab === "macacl" && (
                  <div
                    className="tab-pane fade show active"
                    id="pills-macacl"
                    role="tabpanel"
                    aria-labelledby="pills-macacl-tab"
                  >
                    {/* =============MAC ACL================ */}
                    <div className="row wifi-settings">
                      <div className="col-lg-6">
                        <div className="switch-box">
                          <div className="mb-3">
                            <strong style={{ fontSize: "14px" }}>
                              {localizationStrings?.enableMacInfo}
                              <span className="text-danger">*</span>
                            </strong>
                            <label
                              className="ice-switch"
                              style={{ marginLeft: "16px" }}
                            >
                              <input
                                type="checkbox"
                                checked={enableMacAcl}
                                onChange={(e) =>
                                  setEnableMacAcl(e.target.checked)
                                }
                              />
                              <span className="slider"></span>
                            </label>
                          </div>
                        </div>
                        <div className="col-lg-12 mt-3">
                          <label
                            className="col-form-label"
                            style={{ fontWeight: "600" }}
                          >
                            Select MAC Address
                          </label>
                          <div
                            className="select-mac-input"
                            onClick={() => setShowMacDropdown(!showMacDropdown)}
                            style={{
                              border: "1px solid #d6d6d6",
                              padding: "10px",
                              borderRadius: "6px",
                              cursor: "pointer",
                              backgroundColor: "#fff",
                              color:
                                selectedMacs.length > 0 ? "#000" : "#9aa0ac",
                            }}
                          >
                            {selectedMacs.length > 0
                              ? `${selectedMacs.length} MAC(s) selected`
                              : "Select MAC Address"}
                          </div>

                          {showMacDropdown && (
                            <div
                              className="mac-list-container"
                              style={{
                                border: "1px solid #d6d6d6",
                                borderRadius: "6px",
                                padding: "12px",
                                backgroundColor: "#fff",
                                maxHeight: "150px",
                                overflowY: "auto",
                                marginTop: "5px",
                                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                              }}
                            >
                              {macList && macList.length > 0 ? (
                                macList.map((mac) => (
                                  <label
                                    key={mac}
                                    className="d-flex align-items-center"
                                    style={{
                                      cursor: "pointer",
                                      marginBottom: "10px",
                                      fontSize: "13px",
                                      fontWeight: "800", // Extra bold
                                      color: "#000",
                                      gap: "10px",
                                    }}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={selectedMacs.includes(mac)}
                                      onChange={() => handleMacToggle(mac)}
                                      style={{
                                        width: "16px",
                                        height: "16px",
                                        cursor: "pointer",
                                      }}
                                    />
                                    {mac}
                                  </label>
                                ))
                              ) : (
                                <div
                                  className="text-muted text-center p-2"
                                  style={{
                                    fontSize: "12px",
                                    fontWeight: "bold",
                                  }}
                                >
                                  No clients found
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        <div className="mb-3">
                          <label htmlFor="type" className="col-form-label">
                            {localizationStrings?.type}
                          </label>
                          <select
                            className="form-select"
                            value={remoteAclMode}
                            onChange={(e) =>
                              setRemoteAclMode(Number(e.target.value))
                            }
                          >
                            <option value={0}>
                              {localizationStrings?.localACL}
                            </option>
                            <option value={1}>
                              {localizationStrings?.radiusACL}
                            </option>
                          </select>
                        </div>
                        {enableMacAcl && (
                          <div className="mb-3">
                            <label className="col-form-label">
                              {localizationStrings?.policy}
                              <span className="text-danger">*</span>
                            </label>
                            <select
                              className="form-select"
                              value={macAclStatus}
                              onChange={(e) =>
                                setMacAclStatus(Number(e.target.value))
                              }
                            >
                              <option value={0}>
                                {localizationStrings?.allow}
                              </option>
                              <option value={1}>
                                {localizationStrings?.deny}
                              </option>
                            </select>
                          </div>
                        )}
                      </div>
                      <div className="col-lg-6">
                        <div
                          className="mac-button"
                          style={{ marginTop: "32px" }}
                        >
                          <button
                            type="button"
                            className="text-btn primary-btn"
                            onClick={() => setShowManualModal(true)}
                            disabled
                          >
                            {localizationStrings?.manual}
                          </button>
                          <button
                            type="submit"
                            className="text-btn primary-btn"
                            disabled
                          >
                            {localizationStrings?.removeDevice}
                          </button>
                          <button
                            type="submit"
                            className="text-btn primary-btn"
                            onClick={() => setShowAddDeviceModal(true)}
                            disabled
                          >
                            {localizationStrings?.addDevice}
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="wifi-footer">
                      <button
                        type="button"
                        className="btn btn-outline"
                        onClick={() => navigate(-1)}
                      >
                        {localizationStrings?.cancel}
                      </button>
                      <button
                        type="button"
                        className="text-btn primary-btn"
                        onClick={handleMacAclSave}
                      >
                        {localizationStrings?.save}
                      </button>
                    </div>
                  </div>
                )}
                <Modal
                  show={showSuccess}
                  onHide={() => setShowSuccess(false)}
                  centered
                  className="inc-alert index-2000"
                >
                  <span className="success-icon">
                    <i className="icon done"></i>
                  </span>
                  <h3>{localizationStrings?.success}</h3>
                  <p>{successMessage}</p>
                  <Button
                    onClick={() => {
                      setShowSuccess(false);
                      setMacAclStatus(0);
                      setRemoteAclMode(0);
                      setSelectedMacs([]);
                      setEnableMacAcl(false);
                      setShowMacDropdown(false);
                      setManualDevice({ name: "", mac: "" });
                      navigate("/wifi-config");
                    }}
                  >
                    {localizationStrings?.ok}
                  </Button>
                </Modal>

                {showManualModal && (
                  <div className="customModal">
                    <div className="c-modal-dialog modal-dialog-centered small">
                      <div className="modal-content">
                        <div className="modal-header">
                          <h5 className="modal-title">
                            Manual Access Management
                            <span
                              style={{
                                color: "#ff4d4f",
                                marginLeft: 12,
                                fontSize: 14,
                              }}
                            >
                              <i
                                className="fa fa-ban"
                                style={{ marginRight: 4 }}
                              ></i>
                              Deny
                            </span>
                          </h5>
                          <button
                            type="button"
                            className="btn-close"
                            onClick={() => setShowManualModal(false)}
                          ></button>
                        </div>
                        <div className="modal-body">
                          <div className="mb-3">
                            <label className="form-label">Device Name</label>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Device Name"
                              value={manualDevice.name}
                              onChange={(e) =>
                                setManualDevice({
                                  ...manualDevice,
                                  name: e.target.value,
                                })
                              }
                            />
                          </div>
                          <div className="mb-3">
                            <label className="form-label">MAC Address</label>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="MAC Address"
                              value={manualDevice.mac}
                              onChange={(e) =>
                                setManualDevice({
                                  ...manualDevice,
                                  mac: e.target.value,
                                })
                              }
                            />
                          </div>
                        </div>
                        <div className="modal-footer">
                          <button
                            type="button"
                            className="btn btn-outline"
                            onClick={() => setShowManualModal(false)}
                          >
                            Cancel
                          </button>
                          <button
                            type="submit"
                            className="text-btn primary-btn"
                            onClick={() => {
                              // handle add logic here
                              setShowManualModal(false);
                            }}
                            disabled={!manualDevice.name || !manualDevice.mac}
                          >
                            Add
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {showAddDeviceModal && (
                  <div className="customModal">
                    <div className="c-modal-dialog modal-dialog-centered small">
                      <div
                        className="modal-content"
                        style={{ background: "#fff", color: "#232323" }}
                      >
                        <div className="modal-header">
                          <h5 className="modal-title">
                            Manual Access Management
                            <span
                              style={{
                                color: "#ff4d4f",
                                marginLeft: 12,
                                fontSize: 14,
                              }}
                            >
                              <i
                                className="fa fa-ban"
                                style={{ marginRight: 4 }}
                              ></i>
                              Deny
                            </span>
                          </h5>
                          <button
                            type="button"
                            className="btn-close"
                            onClick={() => setShowAddDeviceModal(false)}
                          ></button>
                        </div>
                        <div
                          style={{
                            background: "#ff2343",
                            color: "#fff",
                            padding: "8px 16px",
                            borderRadius: "4px",
                            margin: "16px 0 0 0",
                            display: "flex",
                            alignItems: "center",
                            fontWeight: 500,
                            fontSize: "15px",
                          }}
                        >
                          <i
                            className="fa fa-exclamation-circle"
                            style={{ marginRight: 8 }}
                          ></i>
                          A network error has occurred. Try again in a few
                          minutes.
                        </div>
                        <div className="modal-body" style={{ paddingTop: 0 }}>
                          <div
                            style={{ margin: "20px 0 10px 0", fontWeight: 500 }}
                          >
                            Add Devices
                          </div>
                          {/* Connected Section */}
                          <div
                            style={{
                              background: "#f6f6f6",
                              borderRadius: "0",
                              borderBottom: "1px solid #e0e0e0",
                              padding: "0",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                padding: "16px",
                              }}
                            >
                              <span style={{ fontWeight: 500 }}>Connected</span>
                              <span
                                style={{ cursor: "pointer" }}
                                onClick={() =>
                                  setAddDeviceCollapse((c) => ({
                                    ...c,
                                    connected: !c.connected,
                                  }))
                                }
                              >
                                <i
                                  className={`fa fa-${
                                    addDeviceCollapse.connected
                                      ? "minus"
                                      : "plus"
                                  }`}
                                ></i>
                              </span>
                            </div>
                            {addDeviceCollapse.connected && (
                              <div
                                style={{
                                  padding: "16px",
                                  color: "#bdbdbd",
                                  fontSize: "14px",
                                }}
                              >
                                All clients currently connected to the network
                              </div>
                            )}
                          </div>
                          {/* Recent Section */}
                          <div
                            style={{
                              background: "#f6f6f6",
                              borderRadius: "0",
                              borderBottom: "1px solid #e0e0e0",
                              padding: "0",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                padding: "16px",
                              }}
                            >
                              <span style={{ fontWeight: 500 }}>Recent</span>
                              <span
                                style={{ cursor: "pointer" }}
                                onClick={() =>
                                  setAddDeviceCollapse((c) => ({
                                    ...c,
                                    recent: !c.recent,
                                  }))
                                }
                              >
                                <i
                                  className={`fa fa-${
                                    addDeviceCollapse.recent ? "minus" : "plus"
                                  }`}
                                ></i>
                              </span>
                            </div>
                            {addDeviceCollapse.recent && (
                              <div
                                style={{
                                  padding: "16px",
                                  color: "#bdbdbd",
                                  fontSize: "14px",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "16px",
                                }}
                              >
                                <span>
                                  No clients currently connected to the network
                                </span>
                                <select
                                  style={{
                                    background: "#f6f6f6",
                                    color: "#232323",
                                    border: "1px solid #e0e0e0",
                                    borderRadius: "4px",
                                    padding: "4px 8px",
                                  }}
                                  value={recentTime}
                                  onChange={(e) =>
                                    setRecentTime(e.target.value)
                                  }
                                >
                                  <option>1 Hour</option>
                                  <option>6 Hours</option>
                                  <option>12 Hours</option>
                                  <option>24 Hours</option>
                                </select>
                              </div>
                            )}
                          </div>
                          {/* Allowed Section */}
                          <div
                            style={{
                              background: "#f6f6f6",
                              borderRadius: "0",
                              borderBottom: "1px solid #e0e0e0",
                              padding: "0",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                padding: "16px",
                              }}
                            >
                              <span style={{ fontWeight: 500 }}>Allowed</span>
                              <span
                                style={{ cursor: "pointer" }}
                                onClick={() =>
                                  setAddDeviceCollapse((c) => ({
                                    ...c,
                                    allowed: !c.allowed,
                                  }))
                                }
                              >
                                <i
                                  className={`fa fa-${
                                    addDeviceCollapse.allowed ? "minus" : "plus"
                                  }`}
                                ></i>
                              </span>
                            </div>
                            {addDeviceCollapse.allowed && (
                              <div
                                style={{
                                  padding: "16px",
                                  color: "#bdbdbd",
                                  fontSize: "14px",
                                }}
                              >
                                All clients currently connected to the network
                              </div>
                            )}
                          </div>
                        </div>
                        <div
                          className="modal-footer"
                          style={{ justifyContent: "center", gap: "16px" }}
                        >
                          <button
                            type="button"
                            className="btn btn-outline"
                            style={{
                              border: "1px solid #232323",
                              color: "#232323",
                              background: "transparent",
                              minWidth: "120px",
                            }}
                            onClick={() => setShowAddDeviceModal(false)}
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            className="text-btn primary-btn"
                            onClick={() => setShowAddDeviceModal(false)}
                          >
                            Deny
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === "trafficPolicies" && (
                  <div
                    className="tab-pane fade show active"
                    id="pills-traffic"
                    role="tabpanel"
                    aria-labelledby="pills-traffic-tab"
                  >
                    {/* =============TRAFFIC================ */}

                    <div className="row wifi-settings">
                      <div className="col-lg-6">
                        <div className="switch-box">
                          <strong>{localizationStrings?.dhcpOfferInfo}</strong>
                          <label className="ice-switch">
                            <input type="checkbox" />
                            <span className="slider"></span>
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default WifiSetting;
